<template>
    <q-layout view="hHh lpR fFf">
        <marketing-nav :mobile-nav-open="mobileNav" @toggle-mobile="mobileNav = !mobileNav" />

        <q-page-container>
            <!-- Hero - payments watermark for the pricing page. -->
            <section class="mkt-hero-light mkt-hero-watermark" style="padding:80px 0 40px;">
                <div class="mkt-hero-arcs" aria-hidden="true">
                    <svg class="mkt-hero-arc mkt-hero-arc--bl" viewBox="0 0 600 400" preserveAspectRatio="xMinYMax slice">
                        <defs>
                            <linearGradient id="arcGradBL" x1="0%" y1="100%" x2="100%" y2="0%">
                                <stop offset="0%" stop-color="#4F46E5" stop-opacity="0" />
                                <stop offset="38%" stop-color="#4F46E5" stop-opacity="1" />
                                <stop offset="100%" stop-color="#7C3AED" stop-opacity="0" />
                            </linearGradient>
                        </defs>
                        <g stroke="url(#arcGradBL)" stroke-width="0.7" fill="none">
                            <path d="M -50,350 Q 150,220 380,200 T 700,170" />
                            <path d="M -50,358 Q 155,230 385,210 T 700,180" />
                            <path d="M -50,366 Q 160,240 390,220 T 700,190" />
                            <path d="M -50,374 Q 165,250 395,230 T 700,200" />
                            <path d="M -50,382 Q 170,260 400,240 T 700,210" />
                            <path d="M -50,390 Q 175,270 405,250 T 700,220" />
                            <path d="M -50,398 Q 180,280 410,260 T 700,230" />
                            <path d="M -50,406 Q 185,290 415,270 T 700,240" />
                            <path d="M -50,414 Q 190,300 420,280 T 700,250" />
                            <path d="M -50,422 Q 195,310 425,290 T 700,260" />
                            <path d="M -50,430 Q 200,320 430,300 T 700,270" />
                            <path d="M -50,438 Q 205,330 435,310 T 700,280" />
                        </g>
                    </svg>
                    <svg class="mkt-hero-arc mkt-hero-arc--tr" viewBox="0 0 600 400" preserveAspectRatio="xMinYMax slice">
                        <defs>
                            <linearGradient id="arcGradTR" x1="0%" y1="100%" x2="100%" y2="0%">
                                <stop offset="0%" stop-color="#0EA5E9" stop-opacity="0" />
                                <stop offset="38%" stop-color="#0EA5E9" stop-opacity="1" />
                                <stop offset="100%" stop-color="#4F46E5" stop-opacity="0" />
                            </linearGradient>
                        </defs>
                        <g stroke="url(#arcGradTR)" stroke-width="0.7" fill="none">
                            <path d="M -50,350 Q 150,220 380,200 T 700,170" />
                            <path d="M -50,358 Q 155,230 385,210 T 700,180" />
                            <path d="M -50,366 Q 160,240 390,220 T 700,190" />
                            <path d="M -50,374 Q 165,250 395,230 T 700,200" />
                            <path d="M -50,382 Q 170,260 400,240 T 700,210" />
                            <path d="M -50,390 Q 175,270 405,250 T 700,220" />
                            <path d="M -50,398 Q 180,280 410,260 T 700,230" />
                            <path d="M -50,406 Q 185,290 415,270 T 700,240" />
                            <path d="M -50,414 Q 190,300 420,280 T 700,250" />
                            <path d="M -50,422 Q 195,310 425,290 T 700,260" />
                            <path d="M -50,430 Q 200,320 430,300 T 700,270" />
                            <path d="M -50,438 Q 205,330 435,310 T 700,280" />
                        </g>
                    </svg>
                </div>
                <div class="mkt-hero-watermark-icon mkt-hero-watermark-icon--right" aria-hidden="true">
                    <q-icon name="payments" />
                </div>
                <div class="mkt-container text-center" style="position:relative;z-index:1;">
                    <h1 class="text-weight-bold q-mb-sm" style="font-size:clamp(2rem,4vw,3rem);color:#0F172A;">
                        Simple, <span class="text-gradient-indigo">Transparent</span> Pricing
                    </h1>
                    <p class="q-mb-lg" style="font-size:1.1rem;max-width:600px;margin:0 auto;color:#475569;">
                        Start with <b>3 free signings a month</b>, forever. Upgrade to a paid plan when you need more volume,
                        team seats, or API access. Switch between monthly and annual billing any time.
                    </p>

                    <!-- Billing toggle -->
                    <div class="row justify-center items-center q-gutter-sm q-mb-md">
                        <span :style="!annual ? 'font-size:0.95rem;color:#0F172A;font-weight:600;' : 'font-size:0.95rem;color:#64748B;'">Monthly</span>
                        <q-toggle v-model="annual" color="primary" />
                        <span :style="annual ? 'font-size:0.95rem;color:#0F172A;font-weight:600;' : 'font-size:0.95rem;color:#64748B;'">
                            Annual
                            <q-badge color="primary" label="Save up to 25%" class="q-ml-xs" />
                        </span>
                    </div>

                    <!-- Quick path: jump to the wizard for visitors who'd rather
                         answer questions than read a comparison table. -->
                    <div class="text-center q-mb-xl mkt-hero-cta-row">
                        <q-btn flat no-caps size="sm"
                               icon="auto_awesome"
                               label="Not sure which plan? Try the plan picker"
                               href="/Home/PlanPicker"
                               class="text-primary"
                               style="font-weight:500;" />
                    </div>
                </div>
            </section>

            <!-- Pricing Cards: three semantic groups stacked vertically.
                 Plan content comes from _pageConfig.plans (server-rendered from
                 Sign_SubscriptionPlans via IMarketingPlanService, stale-while-error
                 cached). Group bucketing comes from each plan's PlanGroup column;
                 `getPlanGroups()` in planData.js handles the grouping + ordering.
                 PlanCard handles the per-tier presentation. -->
            <section v-if="planGroups.length > 0" v-intersection="revealCfg" class="mkt-white reveal-fade-up" style="padding:0 0 60px;margin-top:-30px;">
                <div class="mkt-container">
                    <div v-for="group in planGroups" :key="group.key"
                         :id="`group-${group.key}`"
                         :class="['pricing-group', `pricing-group--${group.key}`]"
                         style="margin-top:40px;scroll-margin-top:96px;">
                        <div class="text-center q-mb-lg">
                            <q-icon :name="group.icon" size="28px" class="text-primary q-mb-xs" />
                            <h3 class="text-weight-bold q-mb-xs" style="font-size:1.5rem;color:#0a0f1a;">{{ group.label }}</h3>
                            <p v-if="group.tagline" class="text-grey-7" style="font-size:0.95rem;max-width:560px;margin:0 auto;">
                                {{ group.tagline }}
                            </p>
                        </div>
                        <div class="row q-col-gutter-lg justify-center">
                            <div v-for="plan in group.plans" :key="plan.tierCode"
                                 :class="cardColClassFor(group)">
                                <plan-card :tier-code="plan.tierCode"
                                           :annual="annual"
                                           :featured="isFeatured(plan)"
                                           :accented="group.key !== 'smb'"
                                           :icon="iconFor(plan, group)"
                                           :cta-label="ctaLabelFor(plan)"
                                           :cta-outline="plan.tierCode === 'free'"
                                           :includes-label="includesLabelFor(plan)" />
                            </div>
                        </div>
                        <q-separator v-if="group.order < planGroups.length - 1" class="q-mt-xl" spaced />
                    </div>
                </div>
            </section>

            <!-- Error: no plans available (Platform.API unreachable + cache cold) -->
            <section v-else v-intersection="revealCfg" class="mkt-white reveal-fade-in" style="padding:60px 0 80px;">
                <div class="mkt-container" style="max-width:560px;">
                    <div class="mkt-pricing-card plan-card--error-standalone" style="text-align:center;padding:48px 24px;border:1px dashed #FCA5A5;background:#FEF2F2;border-radius:12px;">
                        <q-icon name="cloud_off" size="40px" color="negative" class="q-mb-md" />
                        <div class="text-weight-bold q-mb-xs" style="font-size:1.1rem;color:#0a0f1a;">
                            Pricing temporarily unavailable
                        </div>
                        <div class="text-grey-7 q-mb-md" style="font-size:0.9rem;line-height:1.5;">
                            We couldn't load plan data just now. Refresh the page, or
                            <a href="/Home/Support" class="text-primary">contact support</a>
                            if this persists.
                        </div>
                        <q-btn flat no-caps size="sm" label="Refresh" icon="refresh" @click="reloadPage" />
                    </div>
                </div>
            </section>

            <!-- Free-plan spotlight: visual anchor after the pricing matrix.
                 By the time visitors get here they've seen the numbers; the
                 branded slide reinforces that "Free forever" is the actual
                 offer, not a 14-day trial gotcha. -->
            <marketing-spotlight
                slug="spot-free"
                alt="eSign Launchpad Free plan card - three signings per month forever with no credit card required"
                eyebrow-icon="celebration"
                icon-circle-class="mkt-icon-circle--green"
                title="Free, forever. No credit card."
                blurb="Three signings a month on the Free plan, every month, with no card on file. Pay only the $4.99 per-signing overage if you go over - or upgrade to a paid plan when you outgrow it."
                :bullets="['3 free signings every month, forever', 'No credit card to start, ever', 'Upgrade or downgrade any time', 'Same dashboard, same audit trail as paid plans']"
                cta-label="Start free"
                cta-href="/Account/Register" />

            <!-- Inline Plan Picker Wizard -->
            <!-- Same component as the standalone /Home/PlanPicker page - just mounted
                 inline here so visitors who prefer a questionnaire over a matrix get
                 guided help without leaving the page. -->
            <section class="mkt-light reveal-scale" v-intersection="revealCfg" style="padding:60px 0;border-top:1px solid #E2E8F0;">
                <div class="mkt-container">
                    <div class="text-center q-mb-xl">
                        <div class="text-weight-bold" style="font-size:0.85rem;color:#4F46E5;text-transform:uppercase;letter-spacing:1px;">Need help deciding?</div>
                        <h2 class="text-weight-bold q-mt-xs q-mb-sm" style="font-size:1.8rem;color:#0a0f1a;">Answer 5 questions - we'll pick your plan.</h2>
                        <p class="text-grey-7" style="font-size:1rem;max-width:560px;margin:0 auto;">
                            No signup, no email capture. Recommendations update live as you answer.
                        </p>
                    </div>
                    <plan-picker-wizard :max-recommendations="2" />
                </div>
            </section>

            <!-- Feature Comparison Matrix -->
            <section class="mkt-light reveal-fade-up" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container">
                    <div class="text-center q-mb-xl">
                        <h2 class="text-weight-bold q-mb-sm" style="font-size:1.8rem;color:#0a0f1a;">Compare Plans</h2>
                        <p class="text-grey-7" style="font-size:1rem;">See exactly what's included in each plan</p>
                    </div>

                    <div style="overflow-x:auto;">
                        <table class="mkt-comparison-table">
                            <thead>
                                <tr>
                                    <th style="min-width:180px;"></th>
                                    <th v-for="plan in planColumns" :key="plan.planId"
                                        :style="plan.tierCode === 'pro' ? 'color:var(--mkt-indigo);' : ''">
                                        {{ plan.name }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(row, rowIdx) in comparisonRows" :key="rowIdx">
                                    <td class="text-weight-medium" style="color:#333;">{{ row.feature }}</td>
                                    <td v-for="(val, colIdx) in row.values" :key="colIdx">
                                        <q-icon v-if="val === true" name="check_circle" color="positive" size="20px" />
                                        <q-icon v-else-if="val === false" name="remove" color="grey-4" size="20px" />
                                        <span v-else style="font-size:0.85rem;">{{ val }}</span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            <!-- Add-on Pricing -->
            <section class="mkt-white reveal-fade-up" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container-narrow">
                    <div class="text-center q-mb-xl">
                        <h2 class="text-weight-bold q-mb-sm" style="font-size:1.8rem;color:#0a0f1a;">Transparent Add-on Pricing</h2>
                        <p class="text-grey-7" style="font-size:1rem;">No hidden fees. Know exactly what each feature costs.</p>
                    </div>
                    <div style="overflow-x:auto;">
                        <table class="mkt-comparison-table">
                            <thead>
                                <tr>
                                    <th style="min-width:180px;">Add-on</th>
                                    <th v-for="plan in planColumns" :key="plan.planId"
                                        :style="plan.tierCode === 'pro' ? 'color:var(--mkt-indigo);' : ''">
                                        {{ plan.name }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(row, rowIdx) in addonRows" :key="rowIdx">
                                    <td class="text-weight-medium" style="color:#333;">{{ row.addon }}</td>
                                    <td v-for="(val, colIdx) in row.values" :key="colIdx" style="font-size:0.85rem;">{{ val }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            <!-- FAQ -->
            <section class="mkt-white reveal-scale" v-intersection="revealCfg" style="padding:60px 0 80px;">
                <div class="mkt-container-narrow">
                    <div class="text-center q-mb-xl">
                        <h2 class="text-weight-bold q-mb-sm" style="font-size:1.8rem;color:#0a0f1a;">Frequently Asked Questions</h2>
                    </div>
                    <q-list>
                        <q-expansion-item v-for="faq in faqs" :key="faq.q" :label="faq.q"
                            header-class="text-weight-bold" style="border-bottom:1px solid rgba(0,0,0,0.06);">
                            <q-card>
                                <q-card-section class="text-grey-7" style="font-size:0.95rem;line-height:1.7;">
                                    {{ faq.a }}
                                </q-card-section>
                            </q-card>
                        </q-expansion-item>
                    </q-list>
                </div>
            </section>

            <!-- CTA -->
            <section class="mkt-cta-gradient reveal-fade-up" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container text-center">
                    <h2 class="text-white text-weight-bold q-mb-md" style="font-size:1.8rem;">Start signing today</h2>
                    <p class="text-white q-mb-lg" style="opacity:0.9;font-size:1rem;">No credit card required. Cancel anytime.</p>
                    <q-btn color="white" text-color="primary" size="lg" label="Get Started Free" href="/Account/Register"
                        no-caps unelevated style="border-radius:10px;padding:10px 36px;font-weight:600;" />
                </div>
            </section>

            <!-- Footer -->
            <footer class="mkt-light" style="padding:40px 0 20px;border-top:1px solid #E2E8F0;">
                <div class="mkt-container">
                    <div class="row justify-between items-center">
                        <div class="row items-center">
                            <img src="/img/logo-nav.webp" alt="eSign Launchpad" width="82" height="22" style="height: 22px; width: auto;" />
                        </div>
                        <div class="row q-gutter-md">
                            <a href="/Home/Features" class="text-no-decoration" style="font-size:0.8rem;color:#475569;">Features</a>
                            <a href="/Home/SmallBusiness" class="text-no-decoration" style="font-size:0.8rem;color:#475569;">Small Business</a>
                            <a href="/Home/Security" class="text-no-decoration" style="font-size:0.8rem;color:#475569;">Security</a>
                            <a href="/news" class="text-no-decoration" style="font-size:0.8rem;color:#475569;">News</a>
                            <a href="/privacy" class="text-no-decoration" style="font-size:0.8rem;color:#475569;">Privacy</a>
                            <a href="/terms" class="text-no-decoration" style="font-size:0.8rem;color:#475569;">Terms</a>
                        </div>
                        <div style="font-size:0.75rem;color:#64748B;">&copy; {{ currentYear }} eSign Launchpad</div>
                    </div>
                </div>
            </footer>
        </q-page-container>
    </q-layout>
</template>

<style scoped>
</style>

<script>
import { logout } from '@services/authActions.js';
import PlanCard from '@components/marketing/PlanCard.vue';
import PlanPickerWizard from '@components/marketing/PlanPickerWizard.vue';
import MarketingNav from '@components/marketing/MarketingNav.vue';
import MarketingSpotlight from '@components/marketing/MarketingSpotlight.vue';
import { getPlanGroups, getPlans } from '@components/marketing/planData.js';
import { revealMixin } from '@util/reveal.js';

export default {
    name: 'PricingPage',
    mixins: [revealMixin],
    components: { PlanCard, PlanPickerWizard, MarketingNav, MarketingSpotlight },
    data() {
        return {
            isAuthenticated: typeof _AUTHENTICATED !== 'undefined' && _AUTHENTICATED === true,
            mobileNav: false,
            annual: false,
            currentYear: new Date().getFullYear(),

            // Plan cards, comparison matrix, and add-on table are all hydrated
            // live from window._pageConfig.plans (server-rendered from
            // Sign_SubscriptionPlans via IMarketingPlanService, stale-while-error
            // on Platform.API outage). If a plan changes in the DB, the matrix
            // updates automatically; no marketing-copy edit required. If the
            // server couldn't provide plans, PlanCard + the matrix render an
            // empty state rather than synthesizing placeholder data.
            //
            // Per-row helpers live as methods below; the computed properties
            // walk planColumns once and assemble the value array per row.

            faqs: [
                { q: 'Which plan should I pick if I just want to send a few contracts?', a: 'The Free tier includes 3 free signings a month, forever. Additional signings are $4.99 each. If you regularly need more than ~8 signings a month, the Starter plan ($19/mo for 25 signings) works out cheaper.' },
                { q: 'When should I choose Sign & Collect vs Pro?', a: 'Sign & Collect is purpose-built for professional services collecting deposits or retainers alongside the signing. It\'s cheaper than Pro ($39 vs $59) and its 2.5% platform fee covers the payment workflow as the hero feature. Pick Pro instead if you also need bulk send, signing groups, custom branding, or more signings per month.' },
                { q: 'When should I choose Developer vs Pro?', a: 'The Developer tier is for embedding signing into your own product or building internal automations. It\'s $10 cheaper than Pro and includes the API, but skips team features like branding, bulk send, and payments. Pick Pro instead if you need both team workflow AND the API.' },
                { q: 'Do I need a credit card to sign up?', a: 'No. Every new account starts on the Free tier with 3 free signings a month - no subscription, no card required. Additional signings past 3/month are $4.99 each (card only required at that point). Upgrade to a paid plan whenever you\'re ready; that\'s the only other time we ask for payment.' },
                { q: 'Can I change plans at any time?', a: 'Yes. Upgrades take effect immediately and you\'re charged for a new billing period at the new plan\'s price. Downgrades take effect at the end of your current billing period so you don\'t lose any prepaid time.' },
                { q: 'What counts as a "signing"?', a: 'Each signing package you send counts as one signing, regardless of how many pages, documents, or signers are included. A multi-document package with three signers is still one signing.' },
                { q: 'What happens if I exceed my monthly signing limit?', a: 'Additional signings are billed at the per-signing overage rate for your plan - as low as $0.25 on Business for portal-UI sends, $0.35 for API/connector sends. You can set a monthly dollar cap on overage spend in Billing settings if you want a safety brake on automation runs; otherwise overages are tracked and settled with your next renewal.' },
                { q: 'Does the monthly overage cap include sales tax?', a: 'No. The cap and the running spend are tracked pre-tax. Final invoices may include applicable sales tax on top, which varies by your billing jurisdiction. If you want a hard ceiling that includes tax, set the cap roughly 10% below your true budget.' },
                { q: 'How does annual billing work?', a: 'Annual billing locks in a lower per-month rate (saving 17 to 25 percent depending on tier) but is paid up front for the full year. You can switch between monthly and annual at any time from the dashboard.' },
                { q: 'Is there a long-term contract?', a: 'No. Monthly plans bill monthly and can be canceled anytime. Annual plans are prepaid for the year and don\'t auto-renew without your consent at the end of the term.' },
                { q: 'What payment methods do you accept?', a: 'All major credit cards (Visa, Mastercard, American Express, Discover). Larger Business customers can request invoice billing - contact sales.' },
                { q: 'Are e-signatures legally binding?', a: 'Yes. Signatures captured through eSign Launchpad are legally binding under the U.S. ESIGN Act and UETA, plus equivalent laws in most countries. Every completed package includes a tamper-evident hash-chained audit trail and certificate of completion.' }
            ]
        };
    },
    computed: {
        // Live-bucketed plan groups. getPlanGroups() sources from
        // _pageConfig.plans (server-rendered) and returns an empty array if
        // plans weren't available; the template flips to an error state in
        // that case rather than rendering synthesized data.
        planGroups() {
            return getPlanGroups();
        },

        // Flat plan list (already sorted by SortOrder in MarketingPlanService).
        // Drives the comparison-matrix and add-on-table columns so adding or
        // re-ordering a plan in Sign_SubscriptionPlans flows through here
        // without touching marketing copy.
        planColumns() {
            return getPlans();
        },

        comparisonRows() {
            return [
                { feature: 'Signings per month',                   values: this.planColumns.map(p => this.formatSignings(p)) },
                { feature: 'API signings per month',               values: this.planColumns.map(p => this.formatApiSignings(p)) },
                { feature: 'Users included',                       values: this.planColumns.map(p => this.formatUsers(p)) },
                { feature: 'Templates',                            values: this.planColumns.map(p => this.formatTemplates(p)) },
                { feature: 'Embedded signing',                     values: this.planColumns.map(p => !!p.allowEmbeddedSigning) },
                { feature: 'Payment collection',                   values: this.planColumns.map(p => !!p.allowPaymentCollection) },
                // Plan §5.1 - Instant Bank Payments. Available wherever payment
                // collection is. The marketing label is intentionally short for
                // the matrix; the surrounding row tooltips (if present) carry
                // the longer explanation.
                { feature: 'Instant Bank Payments',                values: this.planColumns.map(p => !!p.allowPaymentCollection) },
                { feature: 'Custom branding',                      values: this.planColumns.map(p => !!p.allowBranding) },
                { feature: 'Webhooks',                             values: this.planColumns.map(p => !!p.allowWebhooks) },
                { feature: 'Bulk send',                            values: this.planColumns.map(p => !!p.allowBulkSend) },
                { feature: 'Signing groups',                       values: this.planColumns.map(p => !!p.allowSigningGroups) },
                { feature: 'REST API',                             values: this.planColumns.map(p => !!p.allowApiAccess) },
                { feature: 'SSO / SCIM',                           values: this.planColumns.map(p => !!p.allowSsoScim) },
                { feature: 'SMS authentication',                   values: this.planColumns.map(p => !!p.allowSmsAuth) },
                { feature: 'KBA verification',                     values: this.planColumns.map(p => !!p.allowKbaAuth) },
                { feature: 'ID verification (Persona)',            values: this.planColumns.map(p => !!p.allowIdVerification) },
                // Every plan ships the audit trail; this row stays a constant true
                // because there is no per-plan column for it in Sign_SubscriptionPlans
                // (compliance baseline applies uniformly).
                { feature: 'Hash-chained audit trail',             values: this.planColumns.map(() => true) },
                { feature: 'Platform fee on collected payments',   values: this.planColumns.map(p => this.formatPlatformFee(p)) }
            ];
        },

        addonRows() {
            return [
                { addon: 'ID Verification',                                          values: this.planColumns.map(p => this.formatPerUseCredit(p, 'idv')) },
                { addon: 'KBA Quiz',                                                 values: this.planColumns.map(p => this.formatPerUseCredit(p, 'kba')) },
                { addon: 'SMS Authentication',                                       values: this.planColumns.map(p => this.formatPerUseCredit(p, 'sms')) },
                { addon: 'Additional users (monthly)',                               values: this.planColumns.map(p => this.formatPerUserPrice(p)) },
                { addon: 'Per signing over plan limit (portal UI)',                  values: this.planColumns.map(p => this.formatOverageRate(p, 'web')) },
                { addon: 'Per signing over plan limit (API / Zapier / Make / HubSpot)', values: this.planColumns.map(p => this.formatOverageRate(p, 'api')) },
                { addon: 'Default monthly overage ceiling',                          values: this.planColumns.map(p => this.formatCeiling(p.defaultMaxMonthlyOverageDollars)) },
                { addon: 'Maximum overage ceiling (adjustable in Billing)',          values: this.planColumns.map(p => this.formatCeiling(p.planMaxMonthlyOverageDollars)) }
            ];
        }
    },
    methods: {
        logout,
        reloadPage() {
            if (typeof window !== 'undefined') window.location.reload();
        },

        // ── Group-aware card presentation helpers ──
        // Sizing: 1-plan groups get wide centered cards; 2-plan groups split
        // half-width; 4-plan groups use quarter-width for a 4-across row.
        cardColClassFor(group) {
            const n = group.plans.length;
            if (n === 1) return 'col-12 col-sm-8 col-md-5';
            if (n === 2) return 'col-12 col-sm-8 col-md-5';
            if (n === 3) return 'col-12 col-sm-6 col-md-4';
            return 'col-12 col-sm-6 col-md-3';
        },
        // SMB row: highlight the middle tier as "Most Popular" based on SortOrder
        // (Pro today; if tiers shift in DB, the highlight follows automatically
        // without code changes as long as Pro stays middle-of-pack by price).
        isFeatured(plan) {
            return plan.tierCode === 'pro';
        },
        // Icon choice: individual and developer groups each hold a single card,
        // so it's worth giving those cards a visual identity. SMB cards stay
        // clean (no icon) so the grid reads uniformly.
        iconFor(plan, group) {
            if (plan.tierCode === 'collect') return 'payments';
            if (group.key === 'developer') return 'code';
            return null;
        },
        ctaLabelFor(plan) {
            if (plan.tierCode === 'free') return 'Start Free';
            if (plan.tierCode === 'developer') return 'Start Building';
            return null; // PlanCard defaults to 'Get Started Free'
        },
        includesLabelFor(plan) {
            // Climbing-ladder copy only makes sense inside the SMB group
            // because that's where the upgrade path lives. Individual and
            // Developer groups read as standalone picks.
            const ladder = {
                starter: 'Everything in Free, plus:',
                pro: 'Everything in Starter, plus:',
                business: 'Everything in Pro, plus:'
            };
            return ladder[plan.tierCode] || 'Includes:';
        },

        // ── Comparison-matrix + add-on-table cell formatters ──
        // These derive cell values from the live plan row so the matrix never
        // drifts from Sign_SubscriptionPlans. Return string for displayed
        // text or boolean for the check/dash icons. Return 'n/a' (string) when
        // the feature is conceptually not applicable on this plan rather than
        // 'false' (which renders as a dash icon).

        formatSignings(plan) {
            // Free tier reads as pay-as-you-go because its monthly limit is
            // small (3) and the marketing framing is per-package prepay.
            if (plan.tierCode === 'free') return 'Pay-as-you-go';
            const n = plan.packageLimit;
            if (n === undefined || n === null || n < 0) return 'Unlimited';
            return Number(n).toLocaleString();
        },
        formatApiSignings(plan) {
            const n = plan.apiPackageLimit;
            if (n === undefined || n === null || n <= 0) return false;
            return Number(n).toLocaleString();
        },
        formatUsers(plan) {
            const base = plan.baseUserCount || 1;
            const cap = plan.userLimit;
            if (cap === 1) return '1';
            if (cap === -1) return `${base} (unlimited)`;
            return `${base} (up to ${cap})`;
        },
        formatTemplates(plan) {
            const n = plan.templateLimit;
            if (n === undefined || n === null || n < 0) return 'Unlimited';
            return Number(n).toLocaleString();
        },
        formatPlatformFee(plan) {
            const fee = Number(plan.platformFeePercent || 0);
            if (fee <= 0) return '-';
            const pct = fee * 100;
            // Trim trailing zero on whole percents (2 not 2.00); keep one decimal
            // when meaningful (2.5 not 3 or 2).
            return `${pct % 1 === 0 ? pct.toFixed(0) : pct.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')}%`;
        },
        formatPerUseCredit(plan, kind) {
            // Per-use credit price (IDV / KBA / SMS). The DB column convention
            // uses -1 to mean "not available on this plan"; the API boundary
            // already clamps these to >= 0 (Phase 3), so anything <= 0 here
            // means free or unavailable. SMS specifically: 0 = "included free"
            // for plans like Business; for IDV/KBA, 0 is unusual (not free)
            // so we render as '-'.
            const priceField = `${kind}Price`;
            const freeField = `free${kind.charAt(0).toUpperCase()}${kind.slice(1)}PerMonth`;
            const price = Number(plan[priceField] || 0);
            const free = Number(plan[freeField] || 0);
            if (kind === 'sms') {
                if (price <= 0 && free <= 0) return 'Included (free)';
            } else {
                if (price <= 0) return '-';
            }
            const unit = kind === 'sms' ? 'SMS' : 'use';
            const base = `$${price.toFixed(2)}/${unit}`;
            return free > 0 ? `${base} (${free} free/mo)` : base;
        },
        formatPerUserPrice(plan) {
            const price = Number(plan.perUserMonthlyPrice || 0);
            if (price <= 0) return '-';
            return `$${price % 1 === 0 ? price.toFixed(0) : price.toFixed(2)}/user/mo`;
        },
        formatOverageRate(plan, channel) {
            // channel: 'api' | 'web'. Free tier uses per-package prepay so
            // labels the cell 'n/a' rather than rendering its OveragePerPackage.
            // API channel reads 'n/a' for plans without API access.
            if (plan.tierCode === 'free') return 'n/a';
            if (channel === 'api' && !plan.allowApiAccess) return 'n/a';
            const channelRate = channel === 'api' ? plan.apiOveragePerPackage : plan.webOveragePerPackage;
            const rate = Number((channelRate ?? plan.overagePerPackage) || 0);
            if (rate <= 0) return 'n/a';
            return `$${rate.toFixed(2)}/signing`;
        },
        formatCeiling(value) {
            if (value === undefined || value === null) return 'n/a';
            const n = Number(value);
            if (n <= 0) return 'n/a';
            // Whole-dollar formatting with comma thousands (e.g. $5,000).
            return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
        }
    }
};
</script>
